import { ReactElement } from 'react'
import { FormControlLabel, FormGroup, MenuItem, Switch, Tooltip } from '@mui/material'
import { useAppStore } from '@stores'
import { appStoreInitialState, defaultLoadRenderConfig } from 'src/state/stores/app'
import LoadRenderNumberField from './components/LoadRenderNumberField'

const LoadRenderConfigMenuItems = (): ReactElement => {
  const lineLoadRenderConfig = useAppStore(state => state.loadRenderConfig.lineLoads)
  const pointLoadRenderConfig = useAppStore(state => state.loadRenderConfig.pointLoads)
  const setLineLoadRenderConfig = useAppStore(state => state.setLineLoadRenderConfig)
  const setPointLoadRenderConfig = useAppStore(state => state.setPointLoadRenderConfig)
  const envelopeRenderConfig = useAppStore(state => state.loadRenderConfig.innerForcesEnvelope)
  const setEnvelopeRenderConfig = useAppStore(state => state.setInnerForcesEnvelopeRenderConfig)

  return (
    <>
      <LoadRenderNumberField
        name="lineLoadTextScale"
        label="Textgröße für Linienlasten"
        tooltip={`Textgröße für Linienlasten in der Scene (default: ${defaultLoadRenderConfig.lineLoads.textScale})`}
        value={lineLoadRenderConfig.textScale}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.textScale}
        onChange={textScale => setLineLoadRenderConfig({ textScale })}
        dataCy="line-load-text-scale-input"
      />

      <LoadRenderNumberField
        name="lineLoadFloorplanMaxWidth"
        label="Max. Breite für Linienlasten im Grundriss [m]"
        tooltip={`Breite der größten Linienlast des Projekts im Grundriss, alle anderen werden proportional skaliert (default: ${defaultLoadRenderConfig.lineLoads.floorplanMaxWidth} m)`}
        value={lineLoadRenderConfig.floorplanMaxWidth}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.floorplanMaxWidth}
        onChange={floorplanMaxWidth => setLineLoadRenderConfig({ floorplanMaxWidth })}
        dataCy="line-load-floorplan-max-width-input"
      />

      <MenuItem data-cy="btn-line-load-units-in-scene">
        <FormGroup>
          <Tooltip title="Einheiten für Linienlasten in der Szene anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  checked={lineLoadRenderConfig.showUnitsInScene}
                  onChange={e => setLineLoadRenderConfig({ showUnitsInScene: e.target.checked })}
                />
              }
              label="Einheiten für Linienlasten anzeigen"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <MenuItem data-cy="btn-line-load-unit-new-line">
        <FormGroup>
          <Tooltip title="Einheit für Linienlasten in separater Zeile anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  checked={lineLoadRenderConfig.showUnitInNewLine}
                  onChange={e => setLineLoadRenderConfig({ showUnitInNewLine: e.target.checked })}
                  disabled={!lineLoadRenderConfig.showUnitsInScene}
                />
              }
              label="Einheit für Linienlasten in neuer Zeile"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <LoadRenderNumberField
        name="pointLoadTextScale"
        label="Textgröße für Punktlasten"
        tooltip={`Textgröße für Punktlasten in der Scene (default: ${defaultLoadRenderConfig.pointLoads.textScale})`}
        value={pointLoadRenderConfig.textScale}
        defaultValue={appStoreInitialState.loadRenderConfig.pointLoads.textScale}
        onChange={textScale => setPointLoadRenderConfig({ textScale })}
        dataCy="point-load-text-scale-input"
      />

      <LoadRenderNumberField
        name="pointLoadFloorplanDiameter"
        label="Durchmesser für Punktlasten im Grundriss [m]"
        tooltip={`Durchmesser des Kreises für Punktlasten im Grundriss (default: ${defaultLoadRenderConfig.pointLoads.floorplanDiameter} m)`}
        value={pointLoadRenderConfig.floorplanDiameter}
        defaultValue={appStoreInitialState.loadRenderConfig.pointLoads.floorplanDiameter}
        onChange={floorplanDiameter => setPointLoadRenderConfig({ floorplanDiameter })}
        dataCy="point-load-floorplan-diameter-input"
      />

      <MenuItem data-cy="btn-point-load-units-in-scene">
        <FormGroup>
          <Tooltip title="Einheiten für Punktlasten in der Szene anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  checked={pointLoadRenderConfig.showUnitsInScene}
                  onChange={e => setPointLoadRenderConfig({ showUnitsInScene: e.target.checked })}
                />
              }
              label="Einheiten für Punktlasten anzeigen"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <MenuItem data-cy="btn-point-load-unit-new-line">
        <FormGroup>
          <Tooltip title="Einheit für Punktlasten in separater Zeile anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  checked={pointLoadRenderConfig.showUnitInNewLine}
                  onChange={e => setPointLoadRenderConfig({ showUnitInNewLine: e.target.checked })}
                  disabled={!pointLoadRenderConfig.showUnitsInScene}
                />
              }
              label="Einheit für Punktlasten in neuer Zeile"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <LoadRenderNumberField
        name="envelopeMaxOrdinate"
        label="Max. Ordinate Umhüllende in 3D"
        unit="m"
        tooltip={`Größte Ordinate der Umhüllenden (My, Vz und uz) in der 3D-Ansicht; der größte Wert eines Bauteils wird mit dieser Länge dargestellt (default: ${defaultLoadRenderConfig.innerForcesEnvelope.maxOrdinate} m)`}
        value={envelopeRenderConfig.maxOrdinate}
        defaultValue={appStoreInitialState.loadRenderConfig.innerForcesEnvelope.maxOrdinate}
        onChange={maxOrdinate => setEnvelopeRenderConfig({ maxOrdinate })}
        dataCy="envelope-max-ordinate-input"
      />
    </>
  )
}

export default LoadRenderConfigMenuItems
